import PopupResponsive from '@/components/PopupResponsive';
import Tabs, { TabItemProps } from '@/components/Tabs';
import { useTranslations } from '@/i18n/use-translations';
import { useEffect, useState, useRef, useCallback } from 'react';
import PurePrescriptionForm from './PurePrescriptionForm';
import { env } from '@/utils/env';
import useResponsive from '@/hooks/useResponsive';
import { IS_FR } from '@/utils/constants';

export type PrescriptionParamType = 'sph' | 'cyl' | 'axis' | 'add' | 'prism' | 'pd' | 'od/os';

interface LearnPrescriptionDialogProps {
  open: boolean;
  initTab?: PrescriptionParamType;
  onClose: () => void;
}

export default function LearnPrescriptionDialog({
  open,
  initTab = 'sph',
  onClose,
}: LearnPrescriptionDialogProps) {
  const presT = useTranslations('account.myPrescriptions.table');
  const t = useTranslations('account.myPrescriptions.learnPrescriptionDialog');
  const [activeParam, setActiveParam] = useState<PrescriptionParamType>('sph');
  const [isScrolled, setIsScrolled] = useState(false);
  const { isMobile } = useResponsive();
  const scrollRef = useRef<HTMLElement>(null);
  const tabsItems = [
    {
      key: 'sph',
      label: presT('sph'),
    },
    {
      key: 'cyl',
      label: presT('cyl'),
    },
    {
      key: 'axis',
      label: presT('axis'),
    },
    {
      key: 'add',
      label: presT('add'),
    },
    {
      key: 'pd',
      label: presT('pd'),
    },
    {
      key: 'prism',
      label: presT('prism'),
    },
  ] satisfies TabItemProps[];

  const paramExplainMap = {
    sph: {
      title: t('sph.title'),
      description: t.rich('sph.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        strong: (chunks) => <span className="font-ib inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    cyl: {
      title: t('cyl.title'),
      description: t.rich('cyl.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        strong: (chunks) => <span className="font-ib inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    axis: {
      title: t('axis.title'),
      description: t.rich('axis.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        strong: (chunks) => <span className="font-ib inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    add: {
      title: t('add.title'),
      description: t.rich('add.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        strong: (chunks) => <span className="font-ib inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    prism: {
      title: t('prism.title'),
      description: t.rich('prism.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        strong: (chunks) => <span className="font-ib inline-block">{chunks}</span>,
        br: () => <br />,
      }),
    },
    pd: {
      title: t('pd.title'),
      description: t.rich('pd.description', {
        b: (chunks) => <span className="font-ib my-4 inline-block">{chunks}</span>,
        strong: (chunks) => <span className="font-ib inline-block">{chunks}</span>,
        p: (chunks) => <div className="text-[16px] font-bs py-[16px]">{chunks}</div>,
        br: () => <br />,
        a: (chunks) => (
          <a
            href={env.NEXT_PUBLIC_PD_RULE_PDF}
            className="text-primary-purple f-be"
            target="_blank"
            rel="noreferrer"
            download="zeelool_pd_ruler.pdf"
          >
            {chunks}
          </a>
        ),
      }),
    },
  };
  const confirmClose = () => {
    onClose();
    setActiveParam('sph');
    setIsScrolled(false);
  };

  const handleScroll = useCallback(() => {
    if (scrollRef.current) {
      const { scrollTop } = scrollRef.current;
      setIsScrolled(scrollTop > 0);
    }
  }, []);
  // 打开时同步到 initTab，关闭时重置为 sph，保证从外部 setInitTab + setOpen 后弹窗能正确展示对应 tab
  useEffect(() => {
    if (open) {
      setActiveParam(initTab || 'sph');
      setIsScrolled(false);
    }
  }, [open, initTab]);

  // 添加滚动事件监听器
  useEffect(() => {
    const scrollElement = scrollRef.current;
    if (scrollElement && isMobile) {
      scrollElement.addEventListener('scroll', handleScroll);
      return () => {
        scrollElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll, isMobile]);

  return (
    <PopupResponsive
      open={open}
      onClose={confirmClose}
      title={t('title')}
      height={'70%'}
      sx={{
        '& .MuiDivider-root': {
          display: 'none',
        },
        '& .MuiDialogContent-root': {
          paddingTop: 0,
          paddingInline: '30px',
        },
        '& .MuiDialogTitle-root': {
          paddingBottom: '20px',
        },
        '& .MuiDialog-paper': {
          width: IS_FR ? '780px' : '680px',
          maxWidth: IS_FR ? '780px' : '680px',
        },
        '& .popup-title': {
          borderBottom: 'none',
          paddingBottom: '12px',
        },
        '& .popup-content': {
          paddingBlock: isMobile ? '0' : '16px',
          paddingInline: '0',
        },
      }}
    >
      <div
        className={`bg-white rounded-lg w-full relative ${isMobile ? 'flex flex-col h-full' : ''}`}
      >
        <Tabs
          items={tabsItems}
          activeKey={activeParam}
          defaultActiveKey={'sph'}
          scrollable
          hasTabContent={!isMobile}
          onChange={(key) => setActiveParam(key as PrescriptionParamType)}
          tabsWrapperProps={{
            sx: {
              justifyContent: 'center',
              paddingInline: '14px',
            },
          }}
          sx={{
            '& .tabs-container': {
              justifyContent: 'center',
            },
            '& .MuiTabs-list': {
              gap: isMobile ? '25px' : '40px',
            },
            '& .MuiTab-root': {
              fontSize: '16px',
              color: '#808080',
            },
            '& .MuiTab-root.Mui-selected': {
              color: 'inherit',
            },
          }}
        />
        <section
          ref={scrollRef}
          className={`${isMobile ? `px-[14px] flex-1 overflow-y-auto ${isScrolled ? 'pt-0' : 'pt-[16px]'}` : ''}`}
        >
          <PurePrescriptionForm paramValue={activeParam} />
          <section className="my-[16px] break-words">
            {/* 法语不展示pd标题 */}
            {!(IS_FR && activeParam === 'pd') && (
              <div
                className={`text-[16px] font-bs ${isMobile ? 'py-[6px]' : 'py-[16px]'}`}
                dangerouslySetInnerHTML={{ __html: paramExplainMap[activeParam].title }}
              />
            )}
            <div className="text-[14px] font-br">{paramExplainMap[activeParam].description}</div>
          </section>
        </section>
      </div>
    </PopupResponsive>
  );
}
